/**
 * Shared geometry utilities for hazard area shapes.
 * Used by PropertyMap.js and ModalActivity.js to compute centroids
 * from polygon/rectangle/circle hazard geometry.
 */

/**
 * Compute the centroid of a hazard's shape geometry.
 *
 * @param {Object} point  — initialPoint / hazardActivityInitialPoint object
 *                          with optional shape_type, data_area, circle_center,
 *                          lat, lng fields.
 * @param {Object|null} fallback — fallback { lat, lng } if no geometry found.
 * @returns {{ lat: number, lng: number } | null}
 */
export const getHazardCentroid = (point, fallback = null) => {
  if (!point) return fallback;

  // Simple marker point
  if (point.lat !== undefined && point.lng !== undefined) {
    return { lat: point.lat, lng: point.lng };
  }

  // Polygon / rectangle — compute centroid from data_area
  if (point.data_area && point.data_area.length > 0) {
    let sumLat = 0;
    let sumLng = 0;
    let validCount = 0;
    point.data_area.forEach((coord) => {
      if (typeof coord !== 'string') return;
      const parts = coord.split(',').map(v => parseFloat(v.trim()));
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        sumLat += parts[0];
        sumLng += parts[1];
        validCount++;
      }
    });
    if (validCount > 0) {
      return {
        lat: sumLat / validCount,
        lng: sumLng / validCount,
      };
    }
  }

  // Circle — use circle center
  if (point.circle_center) {
    return { lat: point.circle_center.lat, lng: point.circle_center.lng };
  }

  return fallback;
};

/**
 * Same as getHazardCentroid but returns [lat, lng] array for Leaflet usage.
 *
 * @param {Object} point
 * @param {Array|null} fallback — e.g. [lat, lng] or null
 * @returns {[number, number] | null}
 */
export const getHazardCentroidArray = (point, fallback = null) => {
  const result = getHazardCentroid(point);
  if (result) return [result.lat, result.lng];
  return fallback;
};
